import { ConditionalRender } from '@libs/core';
import { PaletteCategory, TWidget, TWidgetCommonOption } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, ChangeEvent, ClipboardEvent, CSSProperties, FocusEvent, KeyboardEvent } from 'react';
import { useThemeHook } from '../hooks/themeHook';
import { CoreTextView, TCoreTextStyleProps } from '../text/CoreTextView';
import { TCoreInputData } from '../types/data';
import { TCoreInputOptions } from '../types/options';
import { getAlignment, mergeWithTheme, TPaletteState } from '../utils/styles';
import { buildInputCssVarsNew } from './buildInputCssVarsNew';
import styles from './inputNew.module.css';

export type TCoreInputStylePropsNew = Pick<
    TCoreInputOptions,
    | 'fillContainer'
    | 'alignment'
    | 'inputBorderRadius'
    | 'textAlign'
    | 'padding'
    | 'margin'
    | 'main'
    | 'onSurface'
    | 'outline'
    | 'onSurfaceVariant'
    | 'surfaceContainerHighest'
    | 'font'
    | 'fontStyle'
    | 'height'
    | 'hovered'
    | 'variant'
> & {
    // nested state overrides consumed by buildInputCssVarsNew via getValueFromPalette
    label?: TCoreTextStyleProps;
    /** Nested palette override. A flat colour string is not supported — see buildInputCssVarsNew. */
    error?: Partial<PaletteCategory>;
    disabled?: Partial<PaletteCategory>;
    focused?: Partial<PaletteCategory>;
};

type TNativeInputProps = {
    id?: string;
    dataTest?: string;
    inputRef?: React.RefObject<HTMLInputElement | null>;
    type?: string;
    inputMode?: 'text' | 'numeric' | 'decimal' | 'email';
    autoComplete?: string;
    name?: string;
    maxLength?: number;
    required?: boolean;
    tabIndex?: number;
    autoFocus?: boolean;
    readOnly?: boolean;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
    onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    onPaste?: (e: ClipboardEvent<HTMLInputElement>) => void;
    onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
};

export type TCoreInputViewNewProps = {
    widget?: TWidget<TCoreInputData, TCoreInputOptions>;
    uid?: string;
    label?: string;
    value?: string;
    helperText?: string;
    isError?: boolean;
    isFocused?: boolean;
    isFilled?: boolean;
    styleProps?: TCoreInputStylePropsNew;
    styleName?: TWidgetCommonOption['style'];
    style?: React.CSSProperties;
    /** Extra class for the native input; in capture mode it is the only one it carries. */
    className?: string;
    /**
     * Renders the keystroke-capture field: only the `<input>`, without the container, outline and
     * active indicator (and therefore without the palette css vars, which live on the container).
     * OTP and passcode draw their own boxes and hide the input themselves — the widget spec calls
     * this the hidden mode, "purely to capture keystrokes and drive focus".
     */
    isCapture?: boolean;
    children?: React.ReactNode;
} & Pick<TWidgetCommonOption, 'isDisabled' | 'isHide' | 'parentStyleNames'> &
    TNativeInputProps;

/**
 * Ground-up rewrite of the CoreInput visual layer: state (focused / filled / error / disabled)
 * is expressed as data-attributes on the root, so each visual concern (label color, outline,
 * active indicator) can react to exactly the states it needs per the CoreInput wiki spec,
 * instead of a single combined "floated" flag driving everything.
 */
const CoreInputViewNew = ({
    widget,
    uid,
    id,
    dataTest,
    label,
    value,
    helperText,
    isError,
    isDisabled,
    isHide,
    isFocused,
    isFilled,
    styleProps,
    style,
    styleName,
    className,
    isCapture,
    parentStyleNames,
    inputRef,
    type,
    inputMode,
    autoComplete,
    name,
    maxLength,
    required,
    tabIndex,
    autoFocus,
    readOnly,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    onPaste,
    onClick,
    children,
}: TCoreInputViewNewProps) => {
    const baseWidget: TWidget<TCoreInputData, TCoreInputOptions> = widget ?? {
        uid: uid ?? '',
        internalId: null,
        id: '',
        alternativeId: '',
        widgets: [],
        data: {},
        options: {},
        systemMetadata: {},
    };
    const enrichedWidget = {
        ...baseWidget,
        options: { ...baseWidget.options, style: styleName, parentStyleNames },
    };
    const { palette: themePalette } = useThemeHook(enrichedWidget);
    const palette: TCoreInputOptions = mergeWithTheme(themePalette, styleProps as unknown as Partial<TCoreInputOptions>);

    const isFilledResolved = isFilled ?? Boolean(value);
    const isFloated = Boolean(isFocused || isFilledResolved);
    const variant = palette.variant === 'filled' ? 'filled' : 'outlined';
    const state: { isFocused?: boolean } & TPaletteState = { isError, isDisabled, isFocused };

    const controlClassName = isCapture ? className : clsx(styles.control, !isError && !isDisabled && styles.hoverTint, className);

    const nativeInput = (
        <input
            ref={inputRef}
            id={id}
            data-test={dataTest}
            className={controlClassName}
            value={value || ''}
            aria-invalid={isError || undefined}
            disabled={isDisabled}
            type={type}
            inputMode={inputMode}
            autoComplete={autoComplete}
            name={name}
            maxLength={maxLength}
            required={required}
            tabIndex={tabIndex}
            autoFocus={autoFocus}
            readOnly={readOnly}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onClick={onClick}
        />
    );

    if (isCapture) {
        return <Activity mode={isHide ? 'hidden' : 'visible'}>{nativeInput}</Activity>;
    }

    return (
        <Activity mode={isHide ? 'hidden' : 'visible'}>
            <div
                className={styles.root}
                data-test={uid ?? widget?.uid}
                data-variant={variant}
                data-floated={isFloated}
                data-focused={Boolean(isFocused)}
                data-error={Boolean(isError)}
                data-disabled={Boolean(isDisabled)}
                data-full-width={Boolean(palette.fillContainer)}
                style={{ ...(buildInputCssVarsNew(palette, state) as CSSProperties), ...getAlignment(palette.alignment), ...style }}
            >
                <div className={styles.field}>
                    <fieldset className={styles.frame} aria-hidden="true">
                        <ConditionalRender condition={!!label}>
                            <legend className={styles.notch}>
                                <span style={{ fontSize: styleProps?.label?.fontStyle?.fontSize }}>{label}</span>
                            </legend>
                        </ConditionalRender>
                    </fieldset>
                    {label && (
                        <CoreTextView
                            text={label}
                            as="label"
                            isError={isError}
                            isDisabled={isDisabled}
                            className={styles.label}
                            styleProps={{
                                ...(isFocused ? styleProps?.label : {}),
                                fontStyle: {
                                    ...styleProps?.label?.fontStyle,
                                    fontSize: isFocused || isFilled ? styleProps?.label?.fontStyle?.fontSize : palette.fontStyle?.fontSize,
                                },
                                main: 'var(--grey_72_lc)',
                                error: { main: 'var(--pink_100)' },
                                disabled: { main: 'var(--grey_40_lc)' },
                            }}
                        />
                    )}
                    {nativeInput}
                    <span className={styles.indicator} aria-hidden="true" />
                </div>
                {helperText && <span className={styles.helper}>{helperText}</span>}
                {children}
            </div>
        </Activity>
    );
};

export { CoreInputViewNew };
